/**
 * features/finance/glWrite.ts
 * ------------------------------------------------------------------
 * The client side of the general ledger. Mirrors voucherWrite.ts /
 * financeWrite.ts conventions: the wrapped (undefined-stripping) setDoc from
 * @/lib/firebase, timestamps always stamped, one place that knows the shapes.
 *
 * What is deliberately NOT here:
 *
 *   - No `updateJournal`. Journals are append-only; a posted journal is
 *     corrected by `postReversal`, never edited. There is no code path that
 *     mutates a journal's lines, so there is no code path that can quietly
 *     rewrite history.
 *   - No `deleteJournal`, for the same reason.
 *   - No stored balances. Nothing here writes a running total onto an
 *     account — a trial balance is a reduce over lines (Phase 4).
 *
 * Journal numbers come from the same gapless `allocateSequence` the invoices
 * and receipts use, so JNL/2026/000001 cannot repeat or skip.
 *
 * POSTING lives here now (Sep 2026), and with it the two things a posting
 * cannot be correct without: a period lock, so a journal cannot be backdated
 * into a month the school has already reported on, and a gapless per-year
 * sequence, so JNL/FY2026/000001 cannot repeat or skip.
 *
 * Both are taken inside ONE transaction with the write. Allocating a number
 * and then writing separately is how two bursars posting at the same moment
 * end up sharing a journal number — and a duplicated journal number in a set
 * of books is not a display bug, it is two entries nobody can tell apart.
 *
 * Year-end close still to come.
 * ------------------------------------------------------------------ */
import { collection, doc, getDoc, getDocs, runTransaction } from 'firebase/firestore';
import { db, setDoc } from '@/lib/firebase';
import {
  GL_COLLECTIONS, POSTING_MAP_ID, assertBalanced, buildJournal, formatJournalNo,
  journalCounterId, journalDateProblem, periodIdFor, reverseJournal, validateLines,
  type GlAccount, type GlJournal, type GlPeriod, type GlPostingMap, type JournalLine,
  type JournalSourceType,
} from '@/lib/gl';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { auditFromContext } from '@/lib/audit';

const now = () => new Date().toISOString();

/* ============================== accounts ============================== */

export interface AccountInput {
  code: string;
  name: string;
  type: GlAccount['type'];
  parentCode?: string;
  systemKey?: GlAccount['systemKey'];
  isPostable: boolean;
  active: boolean;
  sortOrder: number;
}

/**
 * Create or amend an account. The doc id IS the code, so "changing a code" is
 * a new account plus deactivating the old one — never a rename in place,
 * which would silently re-point every journal line that referenced it.
 */
export async function saveAccount(a: AccountInput, byUid: string): Promise<void> {
  await setDoc(
    doc(db, GL_COLLECTIONS.accounts, a.code),
    { ...a, id: a.code, updatedAt: now(), updatedBy: byUid },
    { merge: true },
  );
}

/** Deactivate rather than delete: a deleted account orphans its history. */
export async function setAccountActive(code: string, active: boolean, byUid: string): Promise<void> {
  await setDoc(
    doc(db, GL_COLLECTIONS.accounts, code),
    { active, updatedAt: now(), updatedBy: byUid },
    { merge: true },
  );
}

export async function savePostingMap(map: Omit<GlPostingMap, 'id'>, byUid: string): Promise<void> {
  await setDoc(
    doc(db, GL_COLLECTIONS.postingMap, POSTING_MAP_ID),
    { ...map, id: POSTING_MAP_ID, updatedAt: now(), updatedBy: byUid },
    { merge: true },
  );
}

export async function fetchPostingMap(): Promise<GlPostingMap | null> {
  const snap = await getDoc(doc(db, GL_COLLECTIONS.postingMap, POSTING_MAP_ID));
  return snap.exists() ? (snap.data() as GlPostingMap) : null;
}


/* ============================== periods ============================== */

/** Months that are closed. Absent from this collection means open. */
export async function fetchClosedPeriods(): Promise<Set<string>> {
  const snap = await getDocs(collection(db, GL_COLLECTIONS.periods));
  return new Set(snap.docs.filter((d) => (d.data() as GlPeriod).status === 'closed').map((d) => d.id));
}

export async function closePeriod(
  monthId: string,
  fy: string,
  actor: { uid?: string; name?: string },
  note?: string,
): Promise<void> {
  await setDoc(doc(db, GL_COLLECTIONS.periods, monthId), {
    id: monthId,
    fy,
    status: 'closed',
    closedAt: now(),
    closedBy: actor.uid,
    closedByName: actor.name,
    ...(note ? { note } : {}),
  } satisfies GlPeriod);
}

/** Reopen a month. Deliberately a delete: "closed" is the only state this
 *  collection records, so an absent document IS an open month, and leaving a
 *  status:'open' row behind would make two things mean the same. */
export async function reopenPeriod(monthId: string): Promise<void> {
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(doc(db, GL_COLLECTIONS.periods, monthId));
}

/* ============================== posting ============================== */

export interface PostJournalInput {
  type: JournalSourceType;
  /** Unique per source. A manual journal passes its own generated id. */
  sourceId: string;
  date: string;
  narration: string;
  lines: JournalLine[];
  actor: { uid?: string; name?: string; email?: string };
}

/**
 * Post a journal.
 *
 * EVERY CHECK RUNS BEFORE THE WRITE, and the write itself is one transaction:
 *
 *   1. the lines are valid on their own terms (an account chosen, an amount,
 *      not both a debit and a credit on one line);
 *   2. they BALANCE — assertBalanced throws otherwise, and a journal object
 *      that does not balance cannot even be constructed;
 *   3. the date falls in a month that is open, and is not in the future;
 *   4. the number comes from a counter read INSIDE the transaction, so two
 *      people posting at the same moment cannot be handed the same one;
 *   5. the id is deterministic off the source, so re-posting the same source
 *      document overwrites rather than double-counting. A manual journal
 *      generates a fresh sourceId, so it is always new.
 *
 * Returns the journal as written.
 */
export async function postJournal(input: PostJournalInput): Promise<GlJournal> {
  const problems = validateLines(input.lines);
  if (problems.length) throw new Error(problems.join(' '));
  assertBalanced(input.lines);

  const closed = await fetchClosedPeriods();
  const dateProblem = journalDateProblem(input.date, closed);
  if (dateProblem) throw new Error(dateProblem);

  const fy = `FY${String(input.date).slice(0, 4)}`;
  const counterRef = doc(db, FINANCE_COLLECTIONS.counters, journalCounterId(fy));

  const journal = await runTransaction(db, async (tx) => {
    const counterSnap = await tx.get(counterRef);
    const seq = (counterSnap.exists() ? Number(counterSnap.data().seq) || 0 : 0) + 1;

    const j = buildJournal({
      type: input.type,
      sourceId: input.sourceId,
      journalNo: formatJournalNo(fy, seq),
      date: String(input.date).slice(0, 10),
      fy,
      narration: input.narration,
      lines: input.lines,
      postedBy: input.actor.uid ?? 'unknown',
    });

    const ref = doc(db, GL_COLLECTIONS.journals, j.id);
    /* Re-posting the same source is an overwrite by design — that is what
       makes a retry safe. A MANUAL journal never lands here twice because it
       carries a fresh sourceId each time. */
    const existing = await tx.get(ref);
    if (existing.exists()) return existing.data() as GlJournal;

    tx.set(counterRef, { seq, updatedAt: now() }, { merge: true });
    tx.set(ref, j);
    return j;
  });

  auditFromContext(input.actor as never, 'gl_journal_posted', 'finance', {
    targetId: journal.id,
    targetType: 'journal',
    details: {
      journalNo: journal.journalNo,
      date: journal.date,
      total: journal.totalDr,
      narration: journal.narration,
      lines: journal.lines.length,
    },
  });

  return journal;
}

/**
 * Reverse a posted journal with a contra.
 *
 * The only way back. The original is never edited and never deleted — it
 * happened, and a set of books where an entry can disappear is a set nobody
 * can sign. The reversal carries its own number and points at what it undoes.
 *
 * The reversal is dated TODAY, not on the original's date: reversing into a
 * month that has been reported on would change a figure somebody has already
 * acted on, which is the very thing period locks exist to prevent. A
 * correction belongs in the month you noticed it.
 */
export async function postReversal(
  original: GlJournal,
  reason: string,
  actor: { uid?: string; name?: string; email?: string },
): Promise<GlJournal> {
  const why = String(reason ?? '').trim();
  if (why.length < 3) throw new Error('Give a reason for the reversal.');
  if (original.reversedBy) throw new Error('That journal has already been reversed.');

  const today = new Date().toISOString().slice(0, 10);
  const closed = await fetchClosedPeriods();
  const dateProblem = journalDateProblem(today, closed);
  if (dateProblem) throw new Error(dateProblem);

  const fy = `FY${today.slice(0, 4)}`;
  const counterRef = doc(db, FINANCE_COLLECTIONS.counters, journalCounterId(fy));
  const originalRef = doc(db, GL_COLLECTIONS.journals, original.id);

  const reversalDoc = await runTransaction(db, async (tx) => {
    const live = await tx.get(originalRef);
    if (!live.exists()) throw new Error('That journal no longer exists.');
    if ((live.data() as GlJournal).reversedBy) throw new Error('That journal has already been reversed.');

    const counterSnap = await tx.get(counterRef);
    const seq = (counterSnap.exists() ? Number(counterSnap.data().seq) || 0 : 0) + 1;

    const r = reverseJournal(original, {
      journalNo: formatJournalNo(fy, seq),
      date: today,
      fy,
      postedBy: actor.uid ?? 'unknown',
      reason: why,
    });

    tx.set(counterRef, { seq, updatedAt: now() }, { merge: true });
    tx.set(doc(db, GL_COLLECTIONS.journals, r.id), r);
    // The one permitted edit to a posted journal, and the rules allow only
    // these keys.
    tx.update(originalRef, { reversed: true, reversedBy: r.id, reversedAt: now() });
    return r;
  });

  auditFromContext(actor as never, 'gl_journal_reversed', 'finance', {
    targetId: original.id,
    targetType: 'journal',
    details: {
      reverses: original.journalNo,
      reversalNo: reversalDoc.journalNo,
      total: original.totalDr,
      reason: why,
    },
  });

  return reversalDoc;
}

/** A fresh source id for a manual journal — never collides with a posted one. */
export function newManualSourceId(): string {
  return `man_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** The period a date lands in, for the UI to show beside the date field. */
export { periodIdFor };
